import { useEffect, useRef } from "react";
import _ from "lodash";
import { evaluateValueRuleById } from "./utils";
const autoSetStateByForm = /* @__PURE__ */ new WeakMap();
const getAutoSetState = (schemaForm) => {
  let state = autoSetStateByForm.get(schemaForm.control);
  if (!state) {
    state = {
      lastApplied: /* @__PURE__ */ new Map(),
      userEdits: /* @__PURE__ */ new Map(),
      originals: /* @__PURE__ */ new Map(),
      writing: /* @__PURE__ */ new Set()
    };
    autoSetStateByForm.set(schemaForm.control, state);
  }
  return state;
};
const useFieldAutoSetValue = ({
  schemaForm,
  rules,
  pathValue,
  valueHandler,
  preview,
  normalizeValue,
  onAutoSet
}) => {
  const onAutoSetRef = useRef(onAutoSet);
  const normalizeValueRef = useRef(normalizeValue);
  onAutoSetRef.current = onAutoSet;
  normalizeValueRef.current = normalizeValue;
  useEffect(() => {
    if (!valueHandler) return;
    const state = getAutoSetState(schemaForm);
    const { lastApplied, userEdits, originals } = state;
    const writeRuleValue = (value) => {
      state.writing.add(pathValue);
      try {
        schemaForm.setValue(pathValue, value);
      } finally {
        state.writing.delete(pathValue);
      }
      onAutoSetRef.current?.(value);
    };
    const replacesTarget = (name) => !name || pathValue.startsWith(`${name}.`) || pathValue.startsWith(`${name}[`);
    let pendingReplacements = 0;
    const applyAutoSetValue = (targetReplaced = false, values, changedName) => {
      if (state.writing.has(pathValue)) return;
      const formValues = values ?? schemaForm.getValues();
      const { matched, value, sourcePending } = evaluateValueRuleById(
        valueHandler,
        formValues,
        rules,
        preview
      );
      const current = _.get(formValues, pathValue);
      const ruleValue = lastApplied.has(pathValue) ? normalizeValueRef.current ? normalizeValueRef.current(lastApplied.get(pathValue)) : lastApplied.get(pathValue) : void 0;
      if (matched) {
        const normalized = normalizeValueRef.current ? normalizeValueRef.current(value) : value;
        const restoresUserEdit = userEdits.has(pathValue) && _.isEqual(current, userEdits.get(pathValue));
        const overwritten = targetReplaced && !restoresUserEdit && !_.isEqual(current, normalized);
        if (overwritten || !_.isEqual(lastApplied.get(pathValue), value)) {
          if (overwritten || !originals.has(pathValue)) {
            originals.set(pathValue, current);
          }
          userEdits.delete(pathValue);
          const written = [];
          let next = { matched: true, value };
          while (next.matched) {
            const candidate = next.value;
            if (written.some((seen) => _.isEqual(seen, candidate))) break;
            written.push(candidate);
            lastApplied.set(pathValue, candidate);
            writeRuleValue(
              normalizeValueRef.current ? normalizeValueRef.current(candidate) : candidate
            );
            next = evaluateValueRuleById(
              valueHandler,
              schemaForm.getValues(),
              rules,
              preview
            );
          }
          if (!next.matched && !next.sourcePending) {
            lastApplied.delete(pathValue);
            userEdits.delete(pathValue);
            originals.delete(pathValue);
          }
        }
      } else if (sourcePending) {
        if (targetReplaced && lastApplied.has(pathValue) && !_.isEqual(current, ruleValue) && !_.isEqual(current, userEdits.get(pathValue))) {
          originals.set(pathValue, current);
        }
      } else {
        if (!targetReplaced && changedName !== void 0 && (state.writing.size > 0 || pendingReplacements > 0)) {
          return;
        }
        const restore = !targetReplaced && pendingReplacements === 0 && state.writing.size === 0 && changedName !== void 0 && changedName !== pathValue && lastApplied.has(pathValue) && originals.has(pathValue) && !userEdits.has(pathValue) && _.isEqual(current, ruleValue);
        const original = originals.get(pathValue);
        lastApplied.delete(pathValue);
        userEdits.delete(pathValue);
        originals.delete(pathValue);
        if (restore && !_.isEqual(current, original)) {
          writeRuleValue(original);
        }
      }
    };
    let active = true;
    let latestValues;
    const subscription = schemaForm.watch((_values, payload) => {
      latestValues = payload.values;
      if (payload.type === "change" && payload.name === pathValue) {
        userEdits.set(
          pathValue,
          _.get(latestValues ?? schemaForm.getValues(), pathValue)
        );
      }
      if (!replacesTarget(payload.name)) {
        applyAutoSetValue(false, latestValues, payload.name);
        return;
      }
      pendingReplacements += 1;
      queueMicrotask(() => {
        if (active) applyAutoSetValue(true, latestValues);
        pendingReplacements -= 1;
      });
    });
    applyAutoSetValue();
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [schemaForm, rules, pathValue, valueHandler, preview]);
};
export {
  useFieldAutoSetValue
};
